if (!customElements.get('clearance-product-tabs')) {
  customElements.define('clearance-product-tabs', class extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('.cpt__tab')];
      this.panels = [...this.querySelectorAll('.cpt__panel')];
      this.toggles = [...this.querySelectorAll('.cpt__accordion-toggle')];
      if (!this.tabs.length) return;

      this.current = this.dataset.initial || this.tabs[0].dataset.tab;
      this.abort = new AbortController();
      this.animations = new Map();
      this.mobile = matchMedia('(max-width: 749px)');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      const options = { signal: this.abort.signal };

      this.tabs.forEach(tab => {
        tab.addEventListener('click', () => this.activate(tab.dataset.tab), options);
        tab.addEventListener('keydown', event => this.onKey(event), options);
      });

      this.toggles.forEach(toggle => toggle.addEventListener('click', () => {
        this.activate(this.current === toggle.dataset.accordion ? null : toggle.dataset.accordion);
      }, options));

      this.querySelectorAll('.cpt__faq-item > summary').forEach(summary => {
        summary.addEventListener('click', event => {
          event.preventDefault();
          this.toggleFaq(summary.parentElement);
        }, options);
      });

      this.setupMobileCategoryAccordions(options);

      this.mobile.addEventListener('change', () => this.configure(), options);
      this.motion.addEventListener('change', () => this.finishAnimations(), options);

      document.addEventListener('shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        const panel = event.target.closest('.cpt__panel') || event.target.closest('.cpt__panel-shell')?.querySelector('.cpt__panel');
        if (panel) this.activate(panel.dataset.panel, false);
        const faq = event.target.closest('.cpt__faq-item');
        if (faq && !faq.open) this.toggleFaq(faq);
      }, options);

      this.classList.add('is-ready');
      this.configure();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.finishAnimations();

      this.querySelectorAll('.cpt__mobile-category-accordion').forEach(accordion => accordion.remove());
      this.querySelectorAll('.cpt__mobile-accordion-source').forEach(source => {
        source.classList.remove('cpt__mobile-accordion-source');
      });
      this.querySelectorAll('[data-mobile-categories-ready]').forEach(shell => {
        delete shell.dataset.mobileCategoriesReady;
      });
    }

    configure() {
      this.finishAnimations();
      this.accordion = this.mobile.matches && this.dataset.mobileLayout === 'accordion';
      this.classList.toggle('is-accordion', this.accordion);
      if (!this.current && !this.accordion) this.current = this.tabs[0].dataset.tab;

      this.panels.forEach((panel, index) => {
        panel.setAttribute('role', this.accordion ? 'region' : 'tabpanel');
        panel.setAttribute('aria-labelledby', this.accordion ? this.toggles[index].id : this.tabs[index].id);
      });

      this.activate(this.current, false);
    }

    onKey(event) {
      const rtl = getComputedStyle(this).direction === 'rtl';
      let index = this.tabs.indexOf(event.currentTarget);
      if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = this.tabs.length - 1;
      else if (event.key === 'ArrowRight') index += rtl ? -1 : 1;
      else if (event.key === 'ArrowLeft') index += rtl ? 1 : -1;
      else return;

      event.preventDefault();
      const tab = this.tabs[(index + this.tabs.length) % this.tabs.length];
      this.activate(tab.dataset.tab);
      tab.focus({ preventScroll: true });

      const nav = tab.parentElement;
      const bounds = nav.getBoundingClientRect();
      const rect = tab.getBoundingClientRect();
      if (rect.left < bounds.left) nav.scrollLeft += rect.left - bounds.left;
      else if (rect.right > bounds.right) nav.scrollLeft += rect.right - bounds.right;
    }

    activate(id, animate = true) {
      if (id !== null && !this.tabs.some(tab => tab.dataset.tab === id)) return;

      const previous = this.current;
      this.current = id;

      this.tabs.forEach(tab => {
        const active = tab.dataset.tab === id;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });

      this.toggles.forEach(toggle => toggle.setAttribute('aria-expanded', String(toggle.dataset.accordion === id)));

      this.panels.forEach(panel => {
        const open = panel.dataset.panel === id;
        const wasHidden = panel.hidden;
        this.cancel(panel);
        panel.inert = !open;

        if (this.accordion && animate && wasHidden === open) {
          const start = open ? 0 : panel.getBoundingClientRect().height;
          panel.hidden = false;
          this.animateHeight(panel, start, open ? panel.scrollHeight : 0, () => {
            panel.hidden = !open;
          });
        } else {
          panel.hidden = !open;
          if (open && animate && previous !== id && this.canAnimate()) {
            this.animate(panel, [
              { opacity: 0, transform: 'translateY(6px)' },
              { opacity: 1, transform: 'translateY(0)' }
            ]);
          }
        }
      });

      if (id && previous !== id && this.mobile.matches) {
        const activePanel = this.panels.find(panel => panel.dataset.panel === id);
        if (activePanel) this.collapseMobileCategoryAccordions(activePanel, false);
      }

      if (id && previous !== id) {
        this.dispatchEvent(new CustomEvent('product-tabs:change', {
          bubbles: true,
          detail: {
            tabId: id,
            panel: this.panels.find(panel => panel.dataset.panel === id)
          }
        }));
      }
    }

    setupMobileCategoryAccordions(options) {
      const supportedTypes = new Set(['description', 'specifications', 'box']);

      this.querySelectorAll('.cpt__panel-shell').forEach(shell => {
        const type = shell.dataset.tabType;
        if (!supportedTypes.has(type) || shell.dataset.mobileCategoriesReady === 'true') return;

        const panel = shell.querySelector('.cpt__panel');
        if (!panel) return;

        const source = [...panel.querySelectorAll('.cpt__rich')]
          .find(node => !node.closest('.cpt__extra') && !node.closest('.cpt__faq-item'));

        if (!source) return;

        const groups = this.parseCategoryContent(source);
        if (groups.length < 2) return;

        const accordion = document.createElement('div');
        accordion.className = 'cpt__faq cpt__mobile-category-accordion';
        accordion.dataset.mobileCategoryAccordion = type;

        groups.forEach((group, index) => {
          const details = document.createElement('details');
          details.className = 'cpt__faq-item cpt__mobile-category-item';

          const summaryId = `${this.id}-${type}-category-trigger-${index + 1}`;
          const answerId = `${this.id}-${type}-category-panel-${index + 1}`;

          const summary = document.createElement('summary');
          summary.id = summaryId;
          summary.setAttribute('aria-expanded', 'false');
          summary.setAttribute('aria-controls', answerId);

          const label = document.createTextNode(group.title);
          const icon = document.createElement('span');
          icon.setAttribute('aria-hidden', 'true');
          icon.textContent = '+';
          summary.append(label, icon);

          const answer = document.createElement('div');
          answer.id = answerId;
          answer.className = 'cpt__faq-answer';
          answer.setAttribute('role', 'region');
          answer.setAttribute('aria-labelledby', summaryId);

          const inner = document.createElement('div');
          inner.className = 'cpt__rich';
          group.nodes.forEach(node => inner.append(node.cloneNode(true)));
          answer.append(inner);

          summary.addEventListener('click', event => {
            event.preventDefault();
            this.toggleFaq(details);
          }, options);

          details.append(summary, answer);
          accordion.append(details);
        });

        source.classList.add('cpt__mobile-accordion-source');
        source.insertAdjacentElement('afterend', accordion);
        shell.dataset.mobileCategoriesReady = 'true';
      });
    }

    parseCategoryContent(source) {
      const roots = [source, ...source.querySelectorAll(':scope > div, :scope > section')];
      let bestGroups = [];

      roots.forEach(root => {
        const groups = [];
        let current = null;

        [...root.children].forEach(node => {
          const heading = this.getCategoryHeading(node);

          if (heading) {
            current = { title: heading.title, nodes: [] };

            const remainder = node.cloneNode(true);
            this.removeCategoryHeading(remainder, heading.selector);

            if (this.hasMeaningfulContent(remainder)) {
              current.nodes.push(remainder);
            }

            groups.push(current);
          } else if (current && this.hasMeaningfulContent(node)) {
            current.nodes.push(node.cloneNode(true));
          }
        });

        if (groups.length > bestGroups.length) bestGroups = groups;
      });

      return bestGroups;
    }

    getCategoryHeading(node) {
      if (!(node instanceof HTMLElement)) return null;

      if (/^H[2-6]$/.test(node.tagName)) {
        const title = node.textContent.trim().replace(/:\s*$/, '');
        return title && title.length <= 80 ? { title, selector: 'heading' } : null;
      }

      const strong = node.querySelector('strong, b');
      if (!strong) return null;

      const strongText = strong.textContent.trim();
      const fullText = node.textContent.trim();

      if (!strongText || !fullText.startsWith(strongText)) return null;

      const title = strongText.replace(/:\s*$/, '').trim();
      if (!title || title.length > 80) return null;

      return { title, selector: 'strong' };
    }

    removeCategoryHeading(node, selector) {
      if (!(node instanceof HTMLElement)) return;

      if (selector === 'heading' && /^H[2-6]$/.test(node.tagName)) {
        node.remove();
        return;
      }

      const strong = node.querySelector('strong, b');
      if (!strong) return;

      let previous = strong.previousSibling;
      while (previous) {
        const candidate = previous.previousSibling;
        const isEmptyText = previous.nodeType === Node.TEXT_NODE && !previous.textContent.trim();
        const isBreak = previous.nodeType === Node.ELEMENT_NODE && previous.nodeName === 'BR';

        if (isEmptyText || isBreak) previous.remove();
        previous = candidate;
      }

      strong.remove();

      const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
      const firstText = walker.nextNode();
      if (firstText) {
        firstText.nodeValue = firstText.nodeValue
          .replace(/^\s*:\s*/, '')
          .replace(/^\s+/, '');
      }

      while (node.firstChild) {
        const first = node.firstChild;
        const isEmptyText = first.nodeType === Node.TEXT_NODE && !first.textContent.trim();
        const isBreak = first.nodeType === Node.ELEMENT_NODE && first.nodeName === 'BR';
        if (!isEmptyText && !isBreak) break;
        first.remove();
      }
    }

    hasMeaningfulContent(node) {
      if (!(node instanceof HTMLElement)) return false;
      return Boolean(
        node.textContent.trim() ||
        node.querySelector('img, video, table, ul, ol, iframe, embed, object')
      );
    }

    collapseMobileCategoryAccordions(panel, animate = false) {
      panel.querySelectorAll('.cpt__mobile-category-item').forEach(details => {
        if (!details.open && details.dataset.closing !== 'true') return;

        const answer = details.querySelector('.cpt__faq-answer');
        const summary = details.querySelector('summary');

        if (answer) this.cancel(answer);
        if (summary) summary.setAttribute('aria-expanded', 'false');

        if (animate && answer && this.canAnimate()) {
          details.dataset.closing = 'true';
          this.animateHeight(answer, answer.getBoundingClientRect().height, 0, () => {
            details.open = false;
            delete details.dataset.closing;
          });
        } else {
          details.open = false;
          delete details.dataset.closing;
          if (answer) {
            answer.style.height = '';
            answer.style.overflow = '';
          }
        }
      });
    }

    canAnimate() {
      return this.dataset.animate === 'true' && !this.motion.matches;
    }

    animate(element, frames, complete = () => {}) {
      if (!this.canAnimate() || typeof element.animate !== 'function') {
        complete();
        return;
      }

      const animation = element.animate(frames, {
        duration: 300,
        easing: 'cubic-bezier(.22,.61,.36,1)'
      });

      this.animations.set(element, { animation, complete });

      animation.onfinish = () => {
        this.animations.delete(element);
        complete();
      };
    }

    animateHeight(element, from, to, complete) {
      element.style.overflow = 'hidden';
      element.style.height = `${from}px`;

      this.animate(element, [
        { height: `${from}px` },
        { height: `${to}px` }
      ], () => {
        element.style.height = '';
        element.style.overflow = '';
        complete();
      });
    }

    cancel(element) {
      const pending = this.animations.get(element);
      if (!pending) return;
      pending.animation.cancel();
      pending.complete();
      this.animations.delete(element);
    }

    finishAnimations() {
      if (this.animations) {
        [...this.animations.keys()].forEach(element => this.cancel(element));
      }
    }

    toggleFaq(details) {
      const answer = details.querySelector('.cpt__faq-answer');
      const opening = !details.open || details.dataset.closing === 'true';
      const from = details.open ? answer.getBoundingClientRect().height : 0;

      this.cancel(answer);
      details.open = true;
      details.dataset.closing = String(!opening);
      details.querySelector('summary').setAttribute('aria-expanded', String(opening));

      this.animateHeight(answer, from, opening ? answer.scrollHeight : 0, () => {
        details.open = opening;
        delete details.dataset.closing;
      });
    }
  });
}
